import { createRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { ItemDetailView } from '@/components/items/ItemDetailView';
import { DRAWER_SEARCH_PARAM_ITEM_ID } from '@/constants/drawer-search';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { parsePositiveIntegerSearchValue } from '@/lib/route-search';
import { itemFilterSearchSchema, normalizeItemFilterSearch, type ItemFilterSearch } from '@/lib/validation';
import { buildLibraryDetailSearch } from '../-library-route-helpers';
import { itemsRoute } from './route';

export const Route = createRoute({
  getParentRoute: () => itemsRoute,
  path: '$itemId',
  validateSearch: (search: Record<string, unknown>): ItemFilterSearch => {
    const parsed = itemFilterSearchSchema.safeParse(search);
    return normalizeItemFilterSearch(parsed.success ? parsed.data : itemFilterSearchSchema.parse({}));
  },
  component: ItemDetailRoutePage,
});

function ItemDetailRoutePage() {
  const { itemId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const isDesktop = useIsDesktop();
  const numericItemId = parsePositiveIntegerSearchValue(itemId) ?? 0;

  useEffect(() => {
    if (!isDesktop || numericItemId <= 0) {
      return;
    }

    void navigate({
      to: '/',
      search: {
        ...buildLibraryDetailSearch(search),
        [DRAWER_SEARCH_PARAM_ITEM_ID]: numericItemId,
      },
      replace: true,
      resetScroll: false,
    });
  }, [isDesktop, navigate, numericItemId, search.query, search.type, search.ui, search.tags]);

  if (isDesktop) {
    return null;
  }

  return (
    <div data-ai-id="library-item-detail-route-page">
      <ItemDetailView itemId={numericItemId} mode="page" backToSearch={buildLibraryDetailSearch(search)} />
    </div>
  );
}
